import { useMemo, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";

// A radial map of ONE index snapshot's reach: the index (MEMORY.md) at the hub,
// every memory THIS version points at on the ring. Node color = capture state —
// solid green = captured (the sidebar lists it; click to open), hollow-dashed =
// ghost (the index names it but no scanned prompt read/wrote it in scope). To
// stay legible at 5 or 150 nodes, edges are hidden by default: the resting view
// is a clean ring of dots, and hovering a node (or the hub) reveals just its
// links. Because it's built from the SELECTED version's pointer lines, scrubbing
// versions visibly changes the map (early index → few nodes; later → many).
const VB = 100;
const CENTER = VB / 2;
const RING = 41;
const HUB_R = 3.2;

export default function MemoryStoreMap({ graph, current, onNavigate, loading = false, linking = false, showCaption = true, maxHeight = 340, anchor = null }) {
  const [hover, setHover] = useState(null); // slug | "MEMORY" | null

  const { positions, leaves } = useMemo(() => {
    const leaves = (graph?.nodes || []).filter((n) => !n.isIndex);
    const positions = new Map([["MEMORY", { x: CENTER, y: CENTER }]]);
    const n = Math.max(1, leaves.length);
    leaves.forEach((node, i) => {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2; // top, clockwise
      positions.set(node.slug, { x: CENTER + RING * Math.cos(a), y: CENTER + RING * Math.sin(a) });
    });
    return { positions, leaves };
  }, [graph]);

  const counts = useMemo(() => {
    let captured = 0;
    let ghost = 0;
    for (const n of leaves) (n.captured ? (captured += 1) : (ghost += 1));
    return { captured, ghost, total: leaves.length };
  }, [leaves]);

  // Edges to draw: those incident to the hovered node (hub hover → all index
  // spokes), PLUS — always — those incident to `anchor` if given. The anchor is
  // the focal memory: keeping its links lit at rest turns the store ring into
  // "this memory's neighborhood" (its index spoke + any [[wikilink]] edges to
  // siblings the hydrated body references) without needing a hover to discover.
  const activeEdges = useMemo(() => {
    if (!graph) return [];
    const out = [];
    const seen = new Set();
    const add = (e) => { const k = `${e.from}->${e.to}`; if (!seen.has(k)) { seen.add(k); out.push(e); } };
    if (anchor && anchor !== "MEMORY") for (const e of graph.edges) if (e.from === anchor || e.to === anchor) add(e);
    if (hover === "MEMORY") for (const e of graph.edges) { if (e.kind === "index") add(e); }
    else if (hover) for (const e of graph.edges) if (e.from === hover || e.to === hover) add(e);
    return out;
  }, [hover, graph, anchor]);
  const litSlugs = useMemo(() => {
    const s = new Set();
    for (const e of activeEdges) { s.add(e.from); s.add(e.to); }
    if (hover) s.add(hover);
    if (anchor) s.add(anchor);
    return s;
  }, [activeEdges, hover, anchor]);

  if (!graph || !leaves.length) {
    return <div className={NOTE_CLASS}>{loading ? "mapping the memory store…" : "This index snapshot points at no memories."}</div>;
  }

  const labelFor = (slug) => (slug === "MEMORY" ? "Memory index" : (graph.nodes.find((n) => n.slug === slug)?.title || slug));
  const hoverNode = hover ? graph.nodes.find((n) => n.slug === hover) : null;
  const hoverBacklinks = hover && hover !== "MEMORY" ? (graph.backlinks.get(hover) || []) : [];

  const leafStyle = (node, isCurrent, lit) => {
    if (loading) return { fill: "var(--line-strong)", stroke: "var(--line-strong)", dash: false }; // capture unknown yet
    if (node.captured) return { fill: "var(--green)", stroke: isCurrent || lit ? "var(--ink)" : "var(--green)", dash: false };
    return { fill: "var(--bg)", stroke: lit ? "var(--ink)" : "var(--muted)", dash: true };
  };

  return (
    <div className="grid gap-[6px]">
      {showCaption ? (
        <div className={NOTE_CLASS}>
          This index snapshot points at {counts.total} {counts.total === 1 ? "memory" : "memories"}
          {loading ? " · resolving captured…" : ` · ${counts.captured} captured · ${counts.ghost} uncaptured`}
          {linking && !loading ? " · linking…" : ""}
        </div>
      ) : null}

      <svg viewBox={`0 0 ${VB} ${VB}`} className="mx-auto block w-full" style={{ aspectRatio: "1 / 1", ...(maxHeight != null ? { maxWidth: maxHeight, maxHeight } : {}) }} role="img" aria-label="Memory store map">
        {/* edges only for the hovered node — keeps the resting view a clean ring */}
        {activeEdges.map((e, i) => {
          const a = positions.get(e.from);
          const b = positions.get(e.to);
          if (!a || !b) return null;
          return (
            <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              style={{ stroke: e.kind === "index" ? "var(--line-strong)" : "var(--green)" }}
              strokeWidth={0.4} opacity={0.6}
            />
          );
        })}

        {/* leaf dots */}
        {leaves.map((node) => {
          const p = positions.get(node.slug);
          const isCurrent = node.slug === current;
          const lit = litSlugs.has(node.slug);
          const dim = hover && !lit ? 0.3 : 1;
          const s = leafStyle(node, isCurrent, lit);
          const r = isCurrent ? 2.9 : lit ? 2.7 : 2.1;
          const clickable = node.captured && !!onNavigate && !isCurrent && !loading;
          return (
            <g key={node.slug} opacity={dim}
              onMouseEnter={() => setHover(node.slug)}
              onMouseLeave={() => setHover((h) => (h === node.slug ? null : h))}
              onClick={() => clickable && onNavigate(node.slug)}
              style={{ cursor: clickable ? "pointer" : "default" }}
            >
              {isCurrent ? <circle cx={p.x} cy={p.y} r={r + 1.5} fill="none" style={{ stroke: "var(--ink)" }} strokeWidth={0.4} /> : null}
              <circle cx={p.x} cy={p.y} r={r} style={{ fill: s.fill, stroke: s.stroke }} strokeWidth={s.dash ? 0.5 : 0.4} strokeDasharray={s.dash ? "0.8 0.8" : undefined} />
              <circle cx={p.x} cy={p.y} r={4.2} fill="transparent" />
            </g>
          );
        })}

        {/* hub (index) */}
        <g onMouseEnter={() => setHover("MEMORY")} onMouseLeave={() => setHover((h) => (h === "MEMORY" ? null : h))}>
          {current === "MEMORY" ? <circle cx={CENTER} cy={CENTER} r={HUB_R + 1.6} fill="none" style={{ stroke: "var(--ink)" }} strokeWidth={0.4} /> : null}
          <circle cx={CENTER} cy={CENTER} r={HUB_R} style={{ fill: "var(--ink)", stroke: "var(--ink)" }} />
          <circle cx={CENTER} cy={CENTER} r={6} fill="transparent" />
        </g>
      </svg>

      {/* hover info card / legend */}
      <div className="min-h-[42px] border-t border-[color:var(--line-soft)] pt-[6px]">
        {hoverNode ? (
          <div className="grid gap-[2px]">
            <div className="flex items-baseline gap-[6px]">
              <span className="text-[12px] font-medium text-[color:var(--ink)]">{hoverNode.title}</span>
              <span className="text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)]">
                {hoverNode.isIndex ? "index" : hoverNode.captured ? "captured" : "uncaptured"}
              </span>
            </div>
            {hoverNode.hook ? <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">{hoverNode.hook}</span> : null}
            <span className="text-[10px] text-[color:var(--muted)]">
              {hoverNode.isIndex
                ? `Points at ${counts.total} ${counts.total === 1 ? "memory" : "memories"}`
                : hoverBacklinks.length
                  ? `Referenced by ${hoverBacklinks.map((b) => labelFor(b.from)).join(", ")}`
                  : "Referenced only by the index"}
              {!hoverNode.isIndex ? (hoverNode.captured ? " · click to open" : " · no captured content") : ""}
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[3px] text-[10px] text-[color:var(--muted)]">
            <span className="flex items-center gap-[4px]"><span className="inline-block h-[7px] w-[7px] rounded-full" style={{ background: "var(--ink)" }} />index (hub)</span>
            <span className="flex items-center gap-[4px]"><span className="inline-block h-[7px] w-[7px] rounded-full" style={{ background: "var(--green)" }} />captured; select to open</span>
            <span className="flex items-center gap-[4px]"><span className="inline-block h-[7px] w-[7px] rounded-full border border-dashed" style={{ borderColor: "var(--muted)" }} />uncaptured</span>
            <span>hover a node for its links</span>
          </div>
        )}
      </div>
    </div>
  );
}
